import * as XLSX from "xlsx-js-style";
import {
  calculateNetFromGross,
  calculateGrossFromNet,
  marginalTaxImpact,
  recoverGrossFromNetDelta,
} from "../utils/marginalTax";

// Flat monthly Emergency Fund contribution per employee. Clients do differ on
// this (22.50 at Viking Shipyard, 27.20 across Raya, 5.00 at KOKI, nothing at
// Cartona), but it ships as one editable default rather than a lookup: the
// cell is a plain input in the exported sheet, and Total Employee Cost,
// Markup and Total Cost are all live formulas off it — so correcting it for a
// client is a single edit that flows through the whole invoice.
const EMERGENCY_FUND_DEFAULT = 24.5;

const usePayrollExport = ({
  t,
  isRTL,
  language,
  selectedMonth,
  // The loaded page only; exports pull EVERY filtered row via fetchAllFiltered.
  filteredPayrollData: loadedRows,
  fetchAllFiltered,
  payrollData,
  totalGrossSalary,
  totalNetSalary,
  totalDeductions,
  averageKPI,
  showAlert,
}) => {
  const roundToHundredth = (value) =>
    Number((Number(value) || 0).toFixed(2));
  const roundToInt = (value) => Math.round(Number(value) || 0);

  const handleExportPayrollExcel = async () => {
    const filteredPayrollData = fetchAllFiltered ? await fetchAllFiltered() : loadedRows;
    try {
      // ── Constants ──────────────────────────────────────────────────────────
      const HEADER_BG = "002060"; // dark navy requested by user
      const HEADER_FG = "FFFFFF";
      const ROW_ALT_BG = "D9E1F2"; // light blue requested by user
      const ROW_DEF_BG = "FFFFFF"; // white – odd data rows (row 3,5,7…)
      const FONT_NAME = "Arial";
      const FONT_SIZE = 11;
      // Every money figure in this export uses 2 decimal places — whole-EGP
      // rounding was throwing away real value on small amounts (Martyrs'
      // Fund at 0.8 rounding to 1 is wrong by a huge relative margin) and,
      // more importantly, hiding genuine cent-level differences everywhere
      // else that matter when reconciling against a source-of-truth
      // spreadsheet. MONEY_FMT_2DP is kept as an alias so any "money2"-typed
      // column keeps working unchanged.
      const MONEY_FMT = "#,##0.00";
      const MONEY_FMT_2DP = "#,##0.00";
      // Gross Salary and Net Salary are kept as whole-number approximations
      // (per explicit user request) even though every other money figure in
      // this export uses 2 decimal places.
      const MONEY_FMT_INT = "#,##0";
      const DATE_FMT = "DD/MM/YYYY";

      const thinBorder = (color = "C0C0C0") => ({
        top: { style: "thin", color: { rgb: color } },
        bottom: { style: "thin", color: { rgb: color } },
        left: { style: "thin", color: { rgb: color } },
        right: { style: "thin", color: { rgb: color } },
      });

      // ── Column definitions (A=0 … Y=24) ───────────────────────────────────
      // type: "text" | "number" | "money" | "formula" | "date"
      // align: "left" | "center" | "right"
      const getLabel = (key, enLabel, arLabel) => {
        const trans = t(key);
        if (trans !== key) return trans;
        return isRTL ? arLabel : enLabel;
      };

      const COLS = [
        { key: "id", label: getLabel("id", "ID", "رقم الموظف"), wch: 12, align: "center", type: "text" },
        { key: "englishName", label: getLabel("englishName", "Employee Name", "اسم الموظف"), wch: 35, align: "left", type: "text" },
        { key: "arabicName", label: getLabel("arabicName", "Arabic Name", "الاسم بالعربي"), wch: 28, align: "right", type: "text" },
        { key: "nationalId", label: getLabel("nationalId", "National ID", "الرقم القومي"), wch: 16, align: "center", type: "text" },
        { key: "phone", label: getLabel("phone", "Phone", "رقم التليفون"), wch: 15, align: "center", type: "text" },
        { key: "department", label: getLabel("department", "Department / Location", "القسم / الموقع"), wch: 22, align: "left", type: "text" },
        { key: "hiringDate", label: getLabel("hiringDate", "Hiring Date", "تاريخ التعيين"), wch: 14, align: "center", type: "date" },
        { key: "accountNumber", label: getLabel("accountNumber", "Account Number", "رقم الحساب"), wch: 20, align: "center", type: "text" },
        { key: "bankName", label: getLabel("bankName", "Bank Name", "اسم البنك"), wch: 22, align: "center", type: "text" },
        { key: "grossSalary", label: getLabel("grossSalary", "Gross Salary", "إجمالي الراتب"), wch: 14, align: "center", type: "moneyInt" },
        { key: "basicSalary", label: getLabel("basicSalary", "Basic Salary", "الراتب الأساسي"), wch: 14, align: "center", type: "money" },
        { key: "netSalary", label: getLabel("netSalary", "Net Salary", "صافي الراتب"), wch: 14, align: "center", type: "moneyInt" },
        { key: "workingDays", label: getLabel("workingDays", "Working Days", "أيام العمل"), wch: 12, align: "center", type: "number" },
        { key: "absenceDays", label: getLabel("absenceDays", "Absence Days", "أيام الغياب"), wch: 12, align: "center", type: "number" },
        { key: "absenceDeduction", label: getLabel("absenceDeduction", "Absence Value", "قيمة الغياب"), wch: 14, align: "center", type: "money" },
        { key: "bonus", label: getLabel("bonus", "Bonus", "المكافأة"), wch: 12, align: "center", type: "money" },
        { key: "overtimeTotal", label: getLabel("overtimeTotal", "Overtime Total", "إجمالي الإضافي"), wch: 14, align: "center", type: "money" },
        { key: "transportation", label: getLabel("transportationAllowance", "Transportation", "بدل الانتقال"), wch: 18, align: "center", type: "money" },
        { key: "meal", label: getLabel("mealAllowance", "Meal Allowance", "بدل الوجبات"), wch: 14, align: "center", type: "money" },
        { key: "mobile", label: getLabel("mobileAllowance", "Mobile Allowance", "بدل الموبايل"), wch: 14, align: "center", type: "money" },
        { key: "bonusAdjustments", label: getLabel("bonusAdjustments", "Bonus Adjustments", "تسويات المكافآت"), wch: 16, align: "center", type: "money" },
        { key: "otherAllowances", label: getLabel("otherAllowances", "Other Allowances", "بدلات أخرى"), wch: 15, align: "center", type: "money" },
        { key: "allowances", label: getLabel("allowances", "Total Allowances", "إجمالي البدلات"), wch: 14, align: "center", type: "money" },
        { key: "penalty", label: getLabel("penalty", "Penalty", "الجزاءات"), wch: 10, align: "center", type: "money" },
        { key: "advancePayment", label: getLabel("advancePayment", "Advance Payment", "السلف"), wch: 14, align: "center", type: "money" },
        { key: "sickLeave", label: getLabel("sickLeave", "Sick Leave 25%", "إجازة مرضية 25%"), wch: 15, align: "center", type: "money" },
        { key: "delayDeduction", label: getLabel("delayDeduction", "Delay", "التأخير"), wch: 10, align: "center", type: "money" },
        { key: "otherDeductions", label: getLabel("otherDeductions", "Other Deductions", "خصومات أخرى"), wch: 15, align: "center", type: "money" },
        { key: "insuranceSalary", label: getLabel("insuranceSalary", "Insurance Salary", "مرتب التأمين"), wch: 16, align: "center", type: "money" },
        { key: "socialInsurance", label: getLabel("socialInsurance", "Social Ins. Employee Share", "تأمين حصة الموظف"), wch: 22, align: "center", type: "money" },
        { key: "insuranceCompanyShare", label: getLabel("insuranceCompanyShare", "Social Ins. Company Share", "تأمين حصة الشركة"), wch: 22, align: "center", type: "money" },
        { key: "taxMonth", label: getLabel("taxMonth", "Tax", "ضريبة الدخل"), wch: 12, align: "center", type: "money" },
        { key: "martyrsFund", label: getLabel("martyrsFund", "Martyrs\' Families Fund", "صندوق أسر الشهداء"), wch: 20, align: "center", type: "money2" },
        { key: "emergencyFund", label: getLabel("emergencyFund", "0.01 Emergency Fund", "صندوق طوارئ 1%"), wch: 18, align: "center", type: "money" },
        { key: "deductions", label: getLabel("deductions", "Total Deductions", "إجمالي الخصومات"), wch: 14, align: "center", type: "money" },

        { key: "totalEmployeeCost", label: getLabel("totalEmployeeCost", "Total Employee Cost", "إجمالي تكلفة الموظف"), wch: 20, align: "center", type: "money" },
        { key: "markup", label: getLabel("markup", "Markup", "هامش الربح"), wch: 12, align: "center", type: "money" },
        { key: "totalCost", label: getLabel("totalCost", "Total Cost", "إجمالي التكلفة"), wch: 14, align: "center", type: "money" },
        { key: "status", label: getLabel("status", "Status", "الحالة"), wch: 12, align: "center", type: "text" },
        { key: "paymentMethod", label: getLabel("paymentMethod", "Payment Method", "طريقة الدفع"), wch: 16, align: "center", type: "text" },
        { key: "paymentDate", label: getLabel("paymentDate", "Payment Date", "تاريخ الدفع"), wch: 14, align: "center", type: "date" },

        // ════════════════════════════════════════════════════════════════
        // Everything below is APPENDED after the original columns — the
        // order of every column above is unchanged, so any sheet/report
        // built against the original layout keeps working. These are pulled
        // straight from each payroll's allowances/deductions line arrays
        // (emp.allowances / emp.deductions), matched by their exact type
        // slug, the same set of types the bulk-import template + Company
        // Configuration support (see payrollController.exportAllowanceDeductionTemplate).
        // ════════════════════════════════════════════════════════════════

        { key: "insuranceNumber", label: getLabel("insuranceNumber", "SI Number", "رقم التأمينات"), wch: 16, align: "center", type: "text" },

        // ── Allowance types not already covered by a named column above ──
        { key: "variableAllowance", label: getLabel("variableAllowance", "Variable Allowance", "بدل متغير"), wch: 16, align: "center", type: "money" },
        { key: "expensesAllowance", label: getLabel("expensesAllowance", "Expenses", "مصاريف"), wch: 14, align: "center", type: "money" },
        { key: "travelAllowance", label: getLabel("travelAllowance", "Travel Allowance", "بدل سفر"), wch: 16, align: "center", type: "money" },
        { key: "eidBonus", label: getLabel("eidBonus", "Eid Bonus", "مكافأة عيد"), wch: 14, align: "center", type: "money" },
        { key: "ramadanBonus", label: getLabel("ramadanBonus", "Ramadan Bonus", "مكافأة رمضان"), wch: 15, align: "center", type: "money" },
        { key: "seasonalBonus", label: getLabel("seasonalBonus", "Seasonal Bonus", "مكافأة موسمية"), wch: 15, align: "center", type: "money" },
        { key: "achievementAllowance", label: getLabel("achievementAllowance", "Achievement", "الإنجاز"), wch: 14, align: "center", type: "money" },
        { key: "kpiAllowance", label: getLabel("kpiAllowance", "KPI", "مؤشرات الأداء"), wch: 12, align: "center", type: "money" },
        { key: "incentiveAllowance", label: getLabel("incentiveAllowance", "Incentive", "حافز"), wch: 14, align: "center", type: "money" },
        { key: "salesIncentive", label: getLabel("salesIncentive", "Sales Incentive", "حافز مبيعات"), wch: 16, align: "center", type: "money" },
        { key: "commission", label: getLabel("commission", "Commission", "عمولة"), wch: 14, align: "center", type: "money" },
        { key: "metryAllowance", label: getLabel("metryAllowance", "Metry", "متري"), wch: 12, align: "center", type: "money" },
        { key: "phoneBillAllowance", label: getLabel("phoneBillAllowance", "Phone Bill", "فاتورة الموبايل"), wch: 14, align: "center", type: "money" },
        { key: "trpAllowance", label: getLabel("trpAllowance", "TRP", "TRP"), wch: 12, align: "center", type: "money" },
        { key: "systemSubscription", label: getLabel("systemSubscription", "System Subscription", "اشتراك نظام"), wch: 18, align: "center", type: "money" },
        { key: "serviceAllowance", label: getLabel("serviceAllowance", "Service Allowance (12%)", "بدل خدمة 12%"), wch: 20, align: "center", type: "money" },
        { key: "settlementAllowance", label: getLabel("settlementAllowance", "Settlement", "تسوية"), wch: 14, align: "center", type: "money" },
        { key: "retroactivePay", label: getLabel("retroactivePay", "Retroactive Pay", "مستحقات بأثر رجعي"), wch: 16, align: "center", type: "money" },
        { key: "performanceAllowance", label: getLabel("performanceAllowance", "Performance", "بدل أداء"), wch: 14, align: "center", type: "money" },
        { key: "holidayCompensation", label: getLabel("holidayCompensation", "Holiday Compensation", "بدل عطلات"), wch: 18, align: "center", type: "money" },
        { key: "guaranteeAllowance", label: getLabel("guaranteeAllowance", "Guarantee", "ضمان"), wch: 14, align: "center", type: "money" },
        { key: "referralProgram", label: getLabel("referralProgram", "Referral Program", "برنامج الترشيحات"), wch: 16, align: "center", type: "money" },
        { key: "eCashWinner", label: getLabel("eCashWinner", "E-Cash Winner", "جائزة E-Cash"), wch: 15, align: "center", type: "money" },
        { key: "adjustmentAllowance", label: getLabel("adjustmentAllowance", "Adjustment", "تسوية إضافية"), wch: 14, align: "center", type: "money" },
        { key: "amanCardRefund", label: getLabel("amanCardRefund", "Refund Amount (Aman Card)", "استرداد كارت أمان"), wch: 22, align: "center", type: "money" },
        { key: "amenGebakRefund", label: getLabel("amenGebakRefund", "Refund Amount (Amen Gebak)", "استرداد أمان چيبك"), wch: 22, align: "center", type: "money" },

        // ── Deduction types not already covered by a named column above ──
        { key: "lateDeduction", label: getLabel("lateDeduction", "Late Deduction", "خصم تأخير"), wch: 14, align: "center", type: "money" },
        { key: "unpaidLeaveDeduction", label: getLabel("unpaidLeaveDeduction", "Unpaid Leave", "إجازة بدون مرتب"), wch: 16, align: "center", type: "money" },
        { key: "otherDeductionType", label: getLabel("otherDeductionType", "Other Deduction", "خصم آخر"), wch: 16, align: "center", type: "money" },
        { key: "amanCardDeduction", label: getLabel("amanCardDeduction", "Aman Card Deduction", "خصم كارت أمان"), wch: 18, align: "center", type: "money" },
        { key: "amenGebakDeduction", label: getLabel("amenGebakDeduction", "Amen Gebak Deduction", "خصم أمان چيبك"), wch: 18, align: "center", type: "money" },
        { key: "premiumCardDeduction", label: getLabel("premiumCardDeduction", "Premium Card Deduction", "خصم بريميوم كارد"), wch: 20, align: "center", type: "money" },
        { key: "computerInstallment", label: getLabel("computerInstallment", "Computer Installment", "قسط كمبيوتر"), wch: 18, align: "center", type: "money" },
        { key: "rayaInstallment", label: getLabel("rayaInstallment", "Raya Installment", "قسط راية"), wch: 16, align: "center", type: "money" },
        { key: "salaryAdvanceDeduction", label: getLabel("salaryAdvanceDeduction", "Salary Advance Deduction", "خصم سلفة"), wch: 20, align: "center", type: "money" },
        { key: "emergencyFundDeduction", label: getLabel("emergencyFundDeduction", "Emergency Fund Deduction", "خصم صندوق طوارئ"), wch: 20, align: "center", type: "money" },
        { key: "medicalInsuranceDeduction", label: getLabel("medicalInsuranceDeduction", "Medical Insurance Deduction", "خصم تأمين طبي"), wch: 20, align: "center", type: "money" },

        // ── Per-line tax breakdown (see marginalTaxImpact in utils/marginalTax.js) ──
        // baseTax/baseMartyrsFund = what tax/Martyrs' Fund would be on this
        // employee's contracted gross ALONE, before any taxable allowance or
        // pre-tax deduction is folded in. Adding every column below back to
        // these two reproduces the existing "Tax"/"Martyrs' Fund" columns
        // exactly (any rounding sliver is folded into the "Other Taxable
        // Allowances" pair so the breakdown always reconciles to the real,
        // already-correct total instead of drifting from it).
        { key: "actualGrossSalary", label: getLabel("actualGrossSalary", "Actual Gross Salary (Before Transportation)", "إجمالي الراتب الفعلي (قبل بدل الانتقال)"), wch: 24, align: "center", type: "money" },
        { key: "baseTax", label: getLabel("baseTax", "Base Tax (Before Allowances)", "الضريبة الأساسية (قبل البدلات)"), wch: 20, align: "center", type: "money" },
        { key: "baseMartyrsFund", label: getLabel("baseMartyrsFund", "Base Martyrs' Fund (Before Allowances)", "صندوق الشهداء الأساسي (قبل البدلات)"), wch: 24, align: "center", type: "money2" },
        { key: "transportationTax", label: getLabel("transportationTax", "Transportation Tax", "ضريبة بدل الانتقال"), wch: 18, align: "center", type: "money" },
        { key: "transportationMartyrsFund", label: getLabel("transportationMartyrsFund", "Transportation Martyrs' Fund", "صندوق الشهداء (بدل الانتقال)"), wch: 22, align: "center", type: "money2" },
        { key: "otherAllowancesTax", label: getLabel("otherAllowancesTax", "Other Taxable Allowances Tax", "ضريبة البدلات الخاضعة الأخرى"), wch: 22, align: "center", type: "money" },
        { key: "otherAllowancesMartyrsFund", label: getLabel("otherAllowancesMartyrsFund", "Other Taxable Allowances Martyrs' Fund", "صندوق الشهداء (بدلات أخرى)"), wch: 24, align: "center", type: "money2" },
        { key: "preTaxDeductionsTaxSaved", label: getLabel("preTaxDeductionsTaxSaved", "Pre-Tax Deductions Tax Saved", "ضريبة موفرة من الخصومات"), wch: 22, align: "center", type: "money" },
        { key: "preTaxDeductionsMFSaved", label: getLabel("preTaxDeductionsMFSaved", "Pre-Tax Deductions Martyrs' Fund Saved", "صندوق الشهداء الموفر من الخصومات"), wch: 24, align: "center", type: "money2" },

        // ── Contract-vs-overtime split ────────────────────────────────────
        // What the employee's package looks like BEFORE any overtime, and
        // what the overtime itself is worth in Gross terms. Together with
        // Basic Salary, Tax and Martyrs' Fund these give the full
        // before/after picture: Old Net → Old Gross → + Overtime Gross →
        // New Gross → New Tax → New Net.
        //
        // "Over Time Gross" has to be recovered rather than read: a Gross
        // overtime line is converted to its net-equivalent when it is saved,
        // and only that net is stored. recoverGrossFromNetDelta inverts the
        // conversion to get back the figure that was actually entered.
        { key: "oldNetSalary", label: getLabel("oldNetSalary", "Old Net Salary", "صافي الراتب قبل الإضافي"), wch: 18, align: "center", type: "money" },
        { key: "oldGrossSalary", label: getLabel("oldGrossSalary", "Old Gross Salary", "إجمالي الراتب قبل الإضافي"), wch: 18, align: "center", type: "money" },
        { key: "oldTaxFromNet", label: getLabel("oldTaxFromNet", "Old Tax from Net", "ضريبة الدخل قبل الإضافي"), wch: 18, align: "center", type: "money" },
        { key: "oldMartyrsFundFromNet", label: getLabel("oldMartyrsFundFromNet", "Old Martyrs' Fund from Net", "صندوق الشهداء قبل الإضافي"), wch: 22, align: "center", type: "money2" },
        { key: "overtimeHoursCount", label: getLabel("overtimeHoursCount", "Over Time Hours", "ساعات الإضافي"), wch: 14, align: "center", type: "number" },
        { key: "overtimeGross", label: getLabel("overtimeGross", "Over Time Gross", "إجمالي الإضافي (جروس)"), wch: 16, align: "center", type: "money" },
      ];

      // ── Helper: 0-based column index → Excel letter(s) ─────────────────────
      const colLetter = (idx) => {
        let s = "";
        let n = idx;
        do {
          s = String.fromCharCode(65 + (n % 26)) + s;
          n = Math.floor(n / 26) - 1;
        } while (n >= 0);
        return s;
      };

      // Map key → column letter for formula building
      const COL = {};
      COLS.forEach((c, i) => { COL[c.key] = colLetter(i); });

      // ── Style builders ──────────────────────────────────────────────────────
      const makeHeaderCell = (label) => ({
        v: label,
        t: "s",
        s: {
          font: { name: FONT_NAME, sz: FONT_SIZE, bold: true, color: { rgb: HEADER_FG } },
          fill: { fgColor: { rgb: HEADER_BG } },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: thinBorder("FFFFFF"),
        },
      });

      const numFmtFor = (colDef) =>
        colDef.type === "moneyInt" ? MONEY_FMT_INT
          : colDef.type === "money2" ? MONEY_FMT_2DP
            : MONEY_FMT;

      const makeDataCell = (value, colDef, rowIdx, liveFormula) => {
        // rowIdx 0 = first data row = Excel row 2 (even → light blue)
        const fillColor = rowIdx % 2 === 0 ? ROW_ALT_BG : ROW_DEF_BG;
        const baseStyle = {
          font: { name: FONT_NAME, sz: FONT_SIZE, color: { rgb: "000000" } },
          fill: { fgColor: { rgb: fillColor } },
          border: thinBorder(),
          alignment: { horizontal: colDef.align, vertical: "center" },
        };

        // A derived column ships as a real Excel formula so editing any input
        // recalculates it in place — but it ALSO carries the value we already
        // computed, as the formula's cached result.
        //
        // Both are needed. A formula written without a cached result displays
        // as 0 until something forces a recalculation: Excel trusts the cache
        // when there is one and shows nothing when there isn't, and preview
        // panes and phone mail apps never calculate at all. That is exactly
        // how a correct Net Salary of 17,833.68 rendered as a blank cell.
        // With the cache present the sheet reads correctly the instant it
        // opens, anywhere, and still recalculates the moment anything is
        // edited. (fullCalcOnLoad is set on the workbook as well, so Excel
        // refreshes the cache on open rather than trusting a stale one.)
        if (liveFormula) {
          return {
            t: "n",
            f: liveFormula,
            v: Number(value) || 0,
            s: { ...baseStyle, numFmt: numFmtFor(colDef) },
          };
        }

        if (colDef.type === "formula") {
          // value is the formula string
          return { t: "n", f: value, s: { ...baseStyle, numFmt: MONEY_FMT } };
        }
        if (colDef.type === "date") {
          return { v: String(value ?? ""), t: "s", s: baseStyle };
        }
        if (colDef.type === "money") {
          return { v: Number(value) || 0, t: "n", s: { ...baseStyle, numFmt: MONEY_FMT } };
        }
        if (colDef.type === "money2") {
          return { v: Number(value) || 0, t: "n", s: { ...baseStyle, numFmt: MONEY_FMT_2DP } };
        }
        if (colDef.type === "moneyInt") {
          return { v: Math.round(Number(value) || 0), t: "n", s: { ...baseStyle, numFmt: MONEY_FMT_INT } };
        }
        if (colDef.type === "number") {
          return { v: Number(value) || 0, t: "n", s: baseStyle };
        }
        // text — ALWAYS force string type (critical for NationalID, Phone)
        return { v: String(value ?? ""), t: "s", s: baseStyle };
      };

      const makeSummaryCell = (value, isFormula = false, isLabel = false, cachedTotal = 0) => {
        const baseStyle = {
          font: { name: FONT_NAME, sz: FONT_SIZE, bold: true, color: { rgb: isLabel ? HEADER_FG : "000000" } },
          fill: { fgColor: { rgb: isLabel ? HEADER_BG : "FFFFFF" } },
          border: thinBorder(),
          alignment: { horizontal: "center", vertical: "center" },
        };
        if (isFormula) {
          // Cached result alongside the SUM, for the same reason as the data
          // cells above — a footer total that reads 0 until you press F9 is
          // worse than no footer at all.
          return { t: "n", f: value, v: Number(cachedTotal) || 0, s: { ...baseStyle, numFmt: MONEY_FMT } };
        }
        if (typeof value === "string") {
          return { v: value, t: "s", s: { ...baseStyle, alignment: { horizontal: "left", vertical: "center" } } };
        }
        return { v: value, t: "n", s: { ...baseStyle, numFmt: MONEY_FMT } };
      };

      // ── The live model, as column groups ────────────────────────────────────
      // These three lists ARE the payroll formula, expressed in export
      // columns, and they mirror payrollService.calculatePayrollValues
      // one-for-one. The exported workbook wires them up as real Excel
      // formulas, so editing any input cell recalculates the whole chain:
      //
      //   Total Allowances = Σ ALLOWANCE_SUM_KEYS
      //   net package      = Basic + Total Allowances − Σ EARNINGS_REDUCTION_KEYS
      //   Gross Salary     = gross-up(net package, Insurance Salary)
      //   Tax / MF / SI    = derived from Gross Salary
      //   Net Salary       = Gross − SI − Tax − MF − Σ POST_TAX_RECOVERY_KEYS
      //
      // Keep these in step with POST_TAX_RECOVERY_DEDUCTION_TYPES in
      // payrollService.js — if a deduction type moves buckets there, its
      // column moves between the last two lists here.
      const ALLOWANCE_SUM_KEYS = [
        "bonus", "overtimeTotal", "transportation", "meal", "mobile", "bonusAdjustments",
        "variableAllowance", "expensesAllowance", "travelAllowance", "eidBonus",
        "ramadanBonus", "seasonalBonus", "achievementAllowance",
        "incentiveAllowance", "salesIncentive", "commission", "metryAllowance",
        "phoneBillAllowance", "trpAllowance", "systemSubscription", "serviceAllowance",
        "settlementAllowance", "retroactivePay", "performanceAllowance",
        "holidayCompensation", "guaranteeAllowance", "referralProgram", "eCashWinner",
        "adjustmentAllowance", "amanCardRefund", "amenGebakRefund", "otherAllowances",
      ];
      // Earnings reductions — the employee simply earned less, so these come
      // off BEFORE the gross-up and are genuinely tax-shielded.
      // "kpiAllowance"/"lateDeduction" are deliberately absent from these
      // lists: they re-display the same underlying line as
      // bonusAdjustments/delayDeduction and would be counted twice.
      const EARNINGS_REDUCTION_KEYS = [
        "absenceDeduction", "penalty", "sickLeave", "delayDeduction",
        "unpaidLeaveDeduction", "otherDeductionType", "otherDeductions",
      ];
      // Recoveries — already earned and already taxed, now being recouped
      // out of take-home, so these come off AFTER tax.
      const POST_TAX_RECOVERY_KEYS = [
        "advancePayment", "amanCardDeduction", "amenGebakDeduction",
        "premiumCardDeduction", "computerInstallment", "rayaInstallment",
        "salaryAdvanceDeduction", "emergencyFundDeduction", "medicalInsuranceDeduction",
      ];

      // ── Define subsets of columns for each tab ──────────────────────────────
      const INFO_KEYS = ["id", "englishName", "arabicName", "nationalId", "phone", "department", "hiringDate", "accountNumber", "bankName", "workingDays", "status", "paymentMethod", "paymentDate"];
      const ALLOWANCES_KEYS = ["id", "englishName", "basicSalary", "bonus", "overtimeTotal", "transportation", "meal", "mobile", "bonusAdjustments",
        "variableAllowance", "expensesAllowance", "travelAllowance", "eidBonus", "ramadanBonus", "seasonalBonus", "achievementAllowance", "kpiAllowance",
        "incentiveAllowance", "salesIncentive", "commission", "metryAllowance", "phoneBillAllowance", "trpAllowance", "systemSubscription", "serviceAllowance",
        "settlementAllowance", "retroactivePay", "performanceAllowance", "holidayCompensation", "guaranteeAllowance", "referralProgram", "eCashWinner",
        "adjustmentAllowance", "amanCardRefund", "amenGebakRefund",
        "otherAllowances", "allowances", "grossSalary"];
      const DEDUCTIONS_KEYS = ["id", "englishName", "basicSalary", "absenceDays", "absenceDeduction", "penalty", "advancePayment", "sickLeave", "delayDeduction", "socialInsurance",
        "unpaidLeaveDeduction", "otherDeductionType", "amanCardDeduction", "amenGebakDeduction", "premiumCardDeduction", "computerInstallment", "rayaInstallment",
        "salaryAdvanceDeduction", "emergencyFundDeduction", "medicalInsuranceDeduction",
        "otherDeductions", "deductions", "netSalary"];
      // Ordered to read as the before/after-overtime story the invoice tells:
      //   Old Net → Old Gross → what's withheld on it → Overtime →
      //   New Gross → what's withheld on that → New Net → cost.
      // The per-allowance tax attribution columns come after, since they
      // explain the total rather than build it.
      const INVOICE_KEYS = ["id", "englishName", "department", "hiringDate", "insuranceNumber",
        // before overtime
        "basicSalary", "oldNetSalary", "oldGrossSalary", "insuranceSalary", "socialInsurance", "insuranceCompanyShare",
        "medicalInsuranceDeduction", "oldTaxFromNet", "oldMartyrsFundFromNet",
        // the overtime itself
        "overtimeHoursCount", "overtimeGross",
        // after overtime
        "grossSalary", "taxMonth", "martyrsFund", "netSalary",
        // cost
        "emergencyFund", "totalEmployeeCost", "markup", "totalCost",
        // attribution detail
        "actualGrossSalary", "baseTax", "baseMartyrsFund",
        "transportationTax", "transportationMartyrsFund",
        "otherAllowancesTax", "otherAllowancesMartyrsFund",
        "preTaxDeductionsTaxSaved", "preTaxDeductionsMFSaved"];

      const INFO_COLS = COLS.filter(c => INFO_KEYS.includes(c.key));
      const ALLOWANCES_COLS = COLS.filter(c => ALLOWANCES_KEYS.includes(c.key));
      const DEDUCTIONS_COLS = COLS.filter(c => DEDUCTIONS_KEYS.includes(c.key));
      // Mapped over INVOICE_KEYS rather than filtered out of COLS, so this tab
      // follows the order the invoice is actually read in (Old Net → Old Gross
      // → Overtime → New Gross → New Net → cost). Filtering would silently
      // keep COLS' own order and ignore that sequence entirely.
      const INVOICE_COLS = INVOICE_KEYS
        .map((k) => COLS.find((c) => c.key === k))
        .filter(Boolean);

      // The Salary Sheet carries every column, so it is the one tab that can
      // host the complete live model. The other tabs compute what they can
      // from their own columns and cross-reference this one for the rest —
      // that way a number is never computed two different ways in two
      // different places, which is how the workbook stays self-consistent
      // no matter which tab you are looking at.
      // Imported payrolls can carry template columns the export has no
      // column for ("Target", "Tier", "VAT 14%", "Total Bill", ...). They are
      // appended to the Salary Sheet, filled for the imported rows only.
      const KNOWN_IMPORTED_KEYS = new Set([...COLS.map((c) => c.key), "monthlyTax", "totalAllowances", "totalDeductions"]);
      const IMPORTED_EXTRA_COLS = (() => {
        const order = [];
        const numeric = new Map();
        filteredPayrollData.forEach((emp) => {
          if (!emp?.isImported || !emp.importedValues) return;
          Object.entries(emp.importedValues).forEach(([k, v]) => {
            if (KNOWN_IMPORTED_KEYS.has(k) || v === null || v === undefined || v === "") return;
            if (!numeric.has(k)) { order.push(k); numeric.set(k, true); }
            if (!Number.isFinite(Number(String(v).replace(/,/g, "")))) numeric.set(k, false);
          });
        });
        return order.map((k, i) => ({
          key: `__imported_${i}`,
          importedHeader: k,
          label: k,
          wch: Math.min(Math.max(14, k.length + 2), 40),
          align: "center",
          type: numeric.get(k) ? "money" : "text",
        }));
      })();
      const SALARY_SHEET_COLS = [
        ...COLS.filter(c => c.key !== "accountNumber" && c.key !== "bankName"),
        ...IMPORTED_EXTRA_COLS,
      ];
      const SALARY_SHEET_NAME = isRTL ? "شيت الرواتب" : "Salary Sheet";
      const salaryColLetters = {};
      SALARY_SHEET_COLS.forEach((c, i) => { salaryColLetters[c.key] = colLetter(i); });

      const exportYear = Number(selectedMonth?.year || new Date().getFullYear());
      const exportMonth = String(Number(selectedMonth?.month || new Date().getMonth() + 1)).padStart(2, "0");

      // ── Period title string ─────────────────────────────────────────────────
      const getPeriodString = () => {
        const fmt = (d) => {
          const dt = new Date(d);
          if (isNaN(dt)) return "";
          return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
        };
        const firstEmp = filteredPayrollData[0];
        if (firstEmp?.payrollStartDate && firstEmp?.payrollEndDate) {
          const start = fmt(firstEmp.payrollStartDate);
          const end = fmt(firstEmp.payrollEndDate);
          if (start && end) {
            return isRTL
              ? `فترة الرواتب: من ${start} إلى ${end}`
              : `Payroll Period: ${start} – ${end}`;
          }
        }
        const y = exportYear;
        const m = Number(exportMonth);
        const firstDay = `01/${String(m).padStart(2, "0")}/${y}`;
        const lastDayDate = new Date(y, m, 0);
        const lastDay = `${String(lastDayDate.getDate()).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
        return isRTL
          ? `فترة الرواتب: من ${firstDay} إلى ${lastDay}`
          : `Payroll Period: ${firstDay} – ${lastDay}`;
      };
      const periodString = getPeriodString();

      // ── The payroll model, as Excel formulas ────────────────────────────────
      // Egyptian income tax is a piecewise-linear function of gross, so the
      // gross-up that inverts it is piecewise-linear too and closes in one
      // step per bracket — no iteration, no circular references, no
      // "enable iterative calculation" dialog. For a bracket with marginal
      // rate r, lower edge K and accumulated tax C at that edge:
      //
      //   gross = (net + E(1−r) − r(20000+K)/12 + C/12) / (1 − r − 0.0005)
      //
      // where E is the employee insurance share and 0.0005 is the Martyrs'
      // Fund rate. Which bracket applies is decided by comparing the net
      // against the net value of each bracket edge — also closed-form, since
      // net rises monotonically with gross. Verified against the binary-search
      // implementation in marginalTax.js across 88,000 (net, insurance-salary)
      // combinations: worst disagreement 0.007 EGP, which is the binary
      // search's own tolerance rather than an error in this expression.
      //
      // ANNUAL_TAXABLE(g, i) below is repeated inline instead of being lifted
      // into a helper cell because LET() needs Excel 2021+, and these files
      // get opened in older Excel, LibreOffice and Google Sheets.


      // ── The payroll model, as Excel formulas ────────────────────────────────
      // Egyptian income tax is a piecewise-linear function of gross, so the
      // gross-up that inverts it is piecewise-linear too and closes in one
      // step per bracket — no iteration, no circular references, no
      // "enable iterative calculation" dialog.
      //
      // By referencing the computed Gross cell directly (here("actualGrossSalary"))
      // rather than inlining the 870-char gross-up formula string 10 times,
      // the tax formula stays under 500 characters (well below Excel's 8,192 limit).
      const xAnnualTaxable = (g, i) => `MAX(0,12*(${g}-${i}*0.11)-20000)`;
      const xTax = (g, i) => {
        const A = xAnnualTaxable(g, i);
        return `(IF(${A}<=40000,0,IF(${A}<=55000,(${A}-40000)*0.1,IF(${A}<=70000,(${A}-55000)*0.15+1500,`
          + `IF(${A}<=200000,(${A}-70000)*0.2+3750,IF(${A}<=400000,(${A}-200000)*0.225+29750,`
          + `(${A}-400000)*0.25+74750))))))/12`;
      };
      const xGrossUp = (p, i) => {
        const E = `(${i}*0.11)`;
        const edge = (grossOffset, taxAtEdge) =>
          `((${E}+${grossOffset})*0.9995-${E}-${taxAtEdge})`;
        const N1 = edge("5000", "0");
        const N2 = edge("6250", "125");
        const N3 = edge("7500", "312.5");
        const N4 = edge("220000/12", "29750/12");
        const N5 = edge("420000/12", "74750/12");
        return `IF(${p}<=${N1},(${p}+${E})/0.9995,`
          + `IF(${p}<=${N2},(${p}+0.9*${E}-500)/0.8995,`
          + `IF(${p}<=${N3},(${p}+0.85*${E}-812.5)/0.8495,`
          + `IF(${p}<=${N4},(${p}+0.8*${E}-1187.5)/0.7995,`
          + `IF(${p}<=${N5},(${p}+0.775*${E}-1645.83333333333)/0.7745,`
          + `(${p}+0.75*${E}-2520.83333333333)/0.7495)))))`;
      };

      // ── Build worksheet Helper ────────────────────────────────────────────────
      // ── Imported payrolls ────────────────────────────────────────────
      // Columns whose value an imported payroll stores itself (payroll
      // columns) — never replaced by a recomputing formula.
      const IMPORTED_STORED_KEYS = ["grossSalary", "netSalary", "taxMonth", "martyrsFund", "allowances", "deductions", "basicSalary", "workingDays"];
      // importedValues key → export column key (same names except these).
      const IMPORTED_VALUE_KEY_TO_COL = {
        insuranceSalary: "insuranceSalary",
        socialInsurance: "socialInsurance",
        insuranceCompanyShare: "insuranceCompanyShare",
        emergencyFund: "emergencyFund",
        totalEmployeeCost: "totalEmployeeCost",
        markup: "markup",
        totalCost: "totalCost",
        actualGrossSalary: "actualGrossSalary",
        baseTax: "baseTax",
        baseMartyrsFund: "baseMartyrsFund",
        transportationTax: "transportationTax",
        transportationMartyrsFund: "transportationMartyrsFund",
        otherAllowancesTax: "otherAllowancesTax",
        otherAllowancesMartyrsFund: "otherAllowancesMartyrsFund",
        preTaxDeductionsTaxSaved: "preTaxDeductionsTaxSaved",
        preTaxDeductionsMFSaved: "preTaxDeductionsMFSaved",
        oldNetSalary: "oldNetSalary",
        oldGrossSalary: "oldGrossSalary",
        oldTaxFromNet: "oldTaxFromNet",
        oldMartyrsFundFromNet: "oldMartyrsFundFromNet",
        overtimeGross: "overtimeGross",
        // payroll-column names, in case an older import stored them here
        monthlyTax: "taxMonth",
        totalAllowances: "allowances",
        totalDeductions: "deductions",
      };
      const importedExtraValue = (emp, header) => {
        if (!emp.isImported || !emp.importedValues) return "";
        const raw = emp.importedValues[header];
        if (raw === undefined || raw === null) return "";
        const n = Number(String(raw).replace(/,/g, ""));
        return Number.isFinite(n) && String(raw).trim() !== "" ? n : raw;
      };

      const buildWorksheet = (sheetCols, periodTitle, isMaster = false) => {
        const ws = {};
        const numEmployees = filteredPayrollData.length;
        const headerRow = 2;
        const firstDataRow = 3;
        const lastDataRow = firstDataRow + numEmployees - 1;
        const summaryRow = lastDataRow + 1; // directly below data rows

        const sheetColLetters = {};
        sheetCols.forEach((c, i) => { sheetColLetters[c.key] = colLetter(i); });

        // Title row (Excel row 1) – merged across all columns
        ws["A1"] = {
          v: periodTitle,
          t: "s",
          s: {
            font: { name: FONT_NAME, sz: 13, bold: true, color: { rgb: HEADER_FG } },
            fill: { fgColor: { rgb: "1F4E79" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: thinBorder("FFFFFF"),
          },
        };

        // Header row (Excel row 2)
        sheetCols.forEach((col, colIdx) => {
          ws[`${colLetter(colIdx)}${headerRow}`] = makeHeaderCell(col.label);
        });

        // Data rows (start at row 3)
        filteredPayrollData.forEach((emp, rowIdx) => {
          const R = firstDataRow + rowIdx;
          let payDateStr = "";
          if (emp.paymentDate) {
            const d = new Date(emp.paymentDate);
            if (!isNaN(d)) {
              payDateStr = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
            } else {
              payDateStr = String(emp.paymentDate);
            }
          } else {
            const now = new Date();
            payDateStr = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
          }

          // ── Raw allowance/deduction line arrays for this payroll ──────────
          // (see payrollMapper.js: mapBackendToUI passes these through
          // untouched, each carrying {type, amount, isTaxable/isPreTax}.)
          const allowanceLines = Array.isArray(emp.allowances) ? emp.allowances : [];
          const deductionLines = Array.isArray(emp.deductions) ? emp.deductions : [];
          const findLine = (list, slug) => list.find((x) => String(x.type || "").toLowerCase() === slug);
          const amtOf = (list, slug) => roundToHundredth(findLine(list, slug)?.amount);

          // ── Per-line tax breakdown ─────────────────────────────────────
          // The backend now computes payroll as ONE net package grossed up
          // ONCE (payrollService.js calculatePayrollValues: netPackage =
          // basicSalary + all allowances − all deductions except
          // advancement, then calculateGrossFromNet on that total). Every
          // stored allowance and deduction amount is a NET EGP figure.
          //
          // So this breakdown mirrors that exactly: start from the contract
          // salary alone, then fold each group in as a NET delta and measure
          // what that delta cost in tax and Martyrs' Fund via
          // marginalTaxImpact. There is no "already-gross" category any
          // more — the old split (flat gross additions in one step,
          // net-inverted Transportation in another) described a formula the
          // backend no longer uses, and its leftovers had to be swept into
          // the Other Allowances bucket by a reconciliation fudge to stop
          // the columns contradicting the real Tax figure.
          //
          // Order is deliberate and matches the reference sheet's own
          // columns: base → other allowances → deductions → Transportation
          // LAST, so "Actual Gross Salary (Before Transportation)" means
          // exactly what its name says. Marginal attribution depends on
          // order, so changing it changes how a bracket crossing is split
          // between the columns (never the total, which is fixed).
          //
          // An insurance salary of 0 is NOT a reason to skip this. Income
          // tax is owed regardless — the insurance salary only sets the
          // social-insurance share, and a zero share simply means nothing is
          // deducted for insurance before the brackets are applied. Gating
          // this block on insSalary > 0 used to blank every breakdown column
          // for those employees while Tax and Net were charged normally: at
          // Viking Shipyard that hit 10 of 102 rows, one of them carrying
          // 5,099.52 of real tax reported as 0.
          const insSalary = Number(emp.insuranceSalary) || 0;
          let baseTax = 0;
          let baseMartyrsFund = 0;
          let transportationTax = 0;
          let transportationMartyrsFund = 0;
          let otherAllowancesTax = 0;
          let otherAllowancesMartyrsFund = 0;
          let preTaxDeductionsTaxSaved = 0;
          let preTaxDeductionsMFSaved = 0;
          // "Actual Gross Salary" — matches the reference sheet's own column
          // of the same name exactly: the gross of everything this employee
          // earns EXCEPT Transportation, which is folded in last.
          let actualGrossSalary = 0;

          if (Number(emp.basicSalary) > 0) {
            // Fold one NET delta into the running gross and report what it
            // cost in tax / Martyrs' Fund. Every group below goes through
            // this same call — allowances and deductions alike, no per-type
            // branching, no "already gross" special case.
            let runningGross = calculateGrossFromNet(Number(emp.basicSalary) || 0, insSalary);
            const foldNet = (netDelta) => {
              if (!netDelta) return { taxDelta: 0, martyrsFundDelta: 0 };
              const r = marginalTaxImpact(runningGross, insSalary, netDelta);
              runningGross += r.grossDelta;
              return r;
            };

            baseTax = calculateNetFromGross(runningGross, insSalary).monthlyTax;
            baseMartyrsFund = runningGross * 0.0005;

            // Transportation is pulled out and held back for last; every
            // other allowance is folded in here as one net delta. Using
            // emp.totalAllowances (rather than re-summing the line array)
            // keeps this consistent with the backend's own totalAllowances,
            // which already dedupes overtime/bonus between the raw payroll
            // columns and same-typed PayrollAllowance lines.
            const transportNet = Number(findLine(allowanceLines, "transportation")?.amount) || 0;
            const otherAllowancesNet = (Number(emp.totalAllowances) || 0) - transportNet;
            {
              const r = foldNet(otherAllowancesNet);
              otherAllowancesTax = r.taxDelta;
              otherAllowancesMartyrsFund = r.martyrsFundDelta;
            }

            // Every deduction except advancement reduces the net package
            // BEFORE the gross-up, so each one genuinely shields tax — the
            // amount of tax it saves is what these two columns report.
            // Advancement is excluded because it is recovered from the final
            // net after Gross Salary is already fixed, so it has no tax
            // effect at all. This mirrors nonAdvancementDbDeductions in
            // calculatePayrollValues exactly.
            const nonAdvancementNet = deductionLines
              .filter((d) => {
                const slug = String(d.type || "").toLowerCase();
                return slug !== "advancement" && slug !== "advance";
              })
              .reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
            {
              const r = foldNet(-nonAdvancementNet);
              // Report as a positive "saved" figure.
              preTaxDeductionsTaxSaved = -r.taxDelta;
              preTaxDeductionsMFSaved = -r.martyrsFundDelta;
            }

            // Everything except Transportation is now in.
            actualGrossSalary = runningGross;

            {
              const r = foldNet(transportNet);
              transportationTax = r.taxDelta;
              transportationMartyrsFund = r.martyrsFundDelta;
            }
          }

          // ── Employer-side costing figures ─────────────────────────────
          // None of these are tracked in the backend — they're pure
          // Invoice-tab arithmetic. Social Insurance Company Share is a flat
          // 18.75% of Insurance Salary, verified against every one of the
          // 280 Aman rows with zero mismatches. The Emergency Fund and the
          // Emergency Fund ships as one editable default
          // (EMERGENCY_FUND_DEFAULT); clients do differ on it — 22.50 at
          // Viking Shipyard, 27.20 across Raya, 5.00 at KOKI, nothing at
          // Cartona — but the cell is a plain input and every cost column
          // below is a live formula off it, so correcting it is one edit.
          const insuranceCompanyShareVal = insSalary > 0 ? insSalary * 0.1875 : 0;
          // Flat monthly figure per employee — it does NOT depend on whether
          // the employee has an Insurance Salary on file. (It used to be
          // zeroed whenever insSalary was 0, which wrongly blanked the column
          // for employees with a genuine insSalary data gap.)
          const emergencyFundVal = EMERGENCY_FUND_DEFAULT;
          const medicalInsuranceDeductionVal = amtOf(deductionLines, "medical_insurance");

          // ── Before / after overtime ───────────────────────────────────
          // "Old" means EVERYTHING THIS EMPLOYEE EARNS EXCEPT OVERTIME — not
          // the bare contract. That is what the invoice means by it: Ahmed
          // Kamal's Old Net of 18,236.83 is his 12,236.40 contract plus a
          // 2,850.43 service allowance and 3,150 of meal allowance, with only
          // the overtime held back. Measuring "Old" from the contract alone
          // gave him 14,728.93 against the invoice's 22,330.74.
          //
          // Overtime Gross then falls out as the difference between the two
          // grossed-up figures — exact by construction, and no inversion
          // needed.
          const RECOVERY_SLUGS = ["aman_card", "amen_gebak", "premium_card",
            "computer_installment", "raya_installment", "salary_advance",
            "emergency_fund", "medical_insurance"];
          const recoveriesSumVal =
            (Number(emp.advancePayment) || 0) +
            RECOVERY_SLUGS.reduce((sum, slug) => sum + amtOf(deductionLines, slug), 0);

          // Recoveries come off after tax, so they are added back to reach the
          // package the gross-up was actually performed on.
          const fullNetPackageVal = (Number(emp.netSalary) || 0) + recoveriesSumVal;
          const overtimeNetVal = Number(emp.overtimeTotal) || 0;
          const oldNetPackageVal = Math.max(fullNetPackageVal - overtimeNetVal, 0);

          const oldGrossSalaryVal =
            oldNetPackageVal > 0 ? calculateGrossFromNet(oldNetPackageVal, insSalary) : 0;
          const oldWithholding =
            oldGrossSalaryVal > 0
              ? calculateNetFromGross(oldGrossSalaryVal, insSalary)
              : { monthlyTax: 0, martyrsFund: 0 };
          const fullGrossVal =
            fullNetPackageVal > 0 ? calculateGrossFromNet(fullNetPackageVal, insSalary) : 0;
          const overtimeGrossVal = Math.max(fullGrossVal - oldGrossSalaryVal, 0);

          // Total Employee Cost = Gross Salary (tax, insurance and Martyrs'
          // Fund already baked in) + the employer's OWN extra costs: its
          // insurance share, the Emergency Fund, and any medical premium.
          // Martyrs' Fund must NOT be added again — it is already inside
          // Gross Salary, and doing so was the bug that inflated this figure.
          const totalEmployeeCostVal =
            roundToHundredth(emp.grossSalary) +
            roundToHundredth(insuranceCompanyShareVal) +
            roundToHundredth(emergencyFundVal) +
            medicalInsuranceDeductionVal;
          const markupVal = Number(emp.markup) || 0;

          const rawValues = {
            id: String(emp.clientCode || emp.infinitycode || emp.infinityCode || emp.code || ""),
            clientCode: String(emp.clientCode || emp.infinitycode || emp.infinityCode || emp.code || ""),
            infinitycode: emp.infinitycode || emp.infinityCode || emp.code || "",
            englishName: emp.englishName || emp.employeeName || emp.sr || "",
            arabicName: emp.arabicName || "",
            nationalId: String(emp.personalInfo?.nationalId || emp.nationalId || ""),
            phone: String(emp.personalInfo?.phone || emp.phoneNumber || ""),
            department: emp.department || "",
            hiringDate: (() => {
              const hd = emp.workInfo?.hiringDate || emp.hiringDate;
              if (!hd) return "";
              const d = new Date(hd);
              if (isNaN(d.getTime())) return String(hd);
              return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
            })(),
            accountNumber: String(emp.bankInfo?.accountNumber || emp.accountNumber || ""),
            bankName: String(emp.bankInfo?.bankName || emp.bankName || ""),
            grossSalary: roundToInt(emp.grossSalary),
            netSalary: roundToInt(emp.netSalary),
            workingDays: Number(emp.workingDays) || 0,
            basicSalary: roundToHundredth(emp.basicSalary),
            bonus: roundToHundredth(emp.bonus),
            overtimeTotal: roundToHundredth(emp.overtimeTotal),
            transportation: roundToHundredth(emp.transportationAllowance),
            meal: roundToHundredth(emp.mealAllowance),
            mobile: roundToHundredth(emp.mobileAllowance),
            bonusAdjustments: roundToHundredth(emp.bonusAdjustments),
            allowances: roundToHundredth(emp.totalAllowances),
            absenceDays: Number(emp.absenceDays) || 0,
            absenceDeduction: amtOf(deductionLines, "absence") || roundToHundredth(emp.absenceDeduction),
            penalty: amtOf(deductionLines, "penalty") || roundToHundredth(emp.penalty),
            advancePayment: amtOf(deductionLines, "advance") || amtOf(deductionLines, "advancement") || roundToHundredth(emp.advancePayment),
            sickLeave: amtOf(deductionLines, "sick_leave_25") || roundToHundredth(emp.sickLeaveDeduction),
            delayDeduction: amtOf(deductionLines, "late_arrival") || amtOf(deductionLines, "delay") || roundToHundredth(emp.delayDeduction),
            deductions: roundToHundredth(emp.totalDeductions),
            insuranceSalary: roundToHundredth(emp.workInfo?.insuranceSalary || emp.insuranceSalary),
            socialInsurance: roundToHundredth(emp.socialInsurance),
            insuranceCompanyShare: roundToHundredth(insuranceCompanyShareVal),
            taxMonth: roundToHundredth(emp.taxMonth),
            martyrsFund: roundToHundredth(emp.martyrsFund),
            emergencyFund: roundToHundredth(emergencyFundVal),
            // Total Employee Cost = Gross Salary (which already has tax,
            // insurance, and Martyrs' Fund baked back in — see emp.grossSalary
            // upstream) + the employer's OWN extra costs: its share of
            // social insurance, the flat Emergency Fund contribution, and
            // any medical insurance deduction. Martyrs' Fund must NOT be
            // added again here — it's already inside Gross Salary, and
            // doing so was the earlier bug that inflated this figure.
            totalEmployeeCost: roundToHundredth(totalEmployeeCostVal),
            markup: roundToHundredth(markupVal),
            totalCost: roundToHundredth(totalEmployeeCostVal + markupVal),

            // ── Before / after overtime ──
            oldNetSalary: roundToHundredth(oldNetPackageVal),
            oldGrossSalary: roundToHundredth(oldGrossSalaryVal),
            oldTaxFromNet: roundToHundredth(oldWithholding.monthlyTax),
            oldMartyrsFundFromNet: roundToHundredth(oldWithholding.martyrsFund),
            overtimeHoursCount: Number(emp.overtimeHours) || 0,
            overtimeGross: roundToHundredth(overtimeGrossVal),
            status: emp.status || "pending",
            paymentMethod: emp.paymentMethod || "Bank Transfer",
            paymentDate: payDateStr,

            // ── Named allowance/deduction types (see the note above the
            // corresponding COLS entries) — pulled straight off this
            // payroll's own line arrays by type slug, nothing derived. ──
            insuranceNumber: String(emp.workInfo?.insuranceNumber || ""),
            variableAllowance: amtOf(allowanceLines, "variable"),
            expensesAllowance: amtOf(allowanceLines, "expenses") || roundToHundredth(emp.expenses),
            travelAllowance: amtOf(allowanceLines, "travel"),
            eidBonus: amtOf(allowanceLines, "eid_bonus"),
            ramadanBonus: amtOf(allowanceLines, "ramadan_bonus"),
            seasonalBonus: amtOf(allowanceLines, "seasonal_bonus"),
            achievementAllowance: amtOf(allowanceLines, "achievement"),
            // Same underlying slug as the existing "bonusAdjustments" column
            // above — shown again here under its own label for the invoice
            // vocabulary, but deliberately left OUT of namedAllowanceKeys
            // below so its amount isn't subtracted from "Other Allowances"
            // twice.
            kpiAllowance: amtOf(allowanceLines, "kpi_adjustment"),
            incentiveAllowance: amtOf(allowanceLines, "incentive"),
            salesIncentive: amtOf(allowanceLines, "sales_incentive"),
            commission: amtOf(allowanceLines, "commission"),
            metryAllowance: amtOf(allowanceLines, "metry"),
            phoneBillAllowance: amtOf(allowanceLines, "phone_bill"),
            trpAllowance: amtOf(allowanceLines, "trp"),
            systemSubscription: amtOf(allowanceLines, "system_subscription"),
            serviceAllowance: amtOf(allowanceLines, "service"),
            settlementAllowance: amtOf(allowanceLines, "settlement"),
            retroactivePay: amtOf(allowanceLines, "retroactive_pay"),
            performanceAllowance: amtOf(allowanceLines, "performance"),
            holidayCompensation: amtOf(allowanceLines, "holiday_compensation"),
            guaranteeAllowance: amtOf(allowanceLines, "guarantee"),
            referralProgram: amtOf(allowanceLines, "referral_program"),
            eCashWinner: amtOf(allowanceLines, "e_cash_winner"),
            adjustmentAllowance: amtOf(allowanceLines, "adjustment"),
            amanCardRefund: amtOf(allowanceLines, "aman_card_refund"),
            amenGebakRefund: amtOf(allowanceLines, "amen_gebak_refund"),

            // Same underlying slug as the existing "delayDeduction" column
            // above — kept OUT of namedDeductionKeys below for the same
            // no-double-subtraction reason as kpiAllowance/bonusAdjustments.
            lateDeduction: amtOf(deductionLines, "late_arrival"),
            unpaidLeaveDeduction: amtOf(deductionLines, "unpaid_leave"),
            otherDeductionType: amtOf(deductionLines, "other"),
            amanCardDeduction: amtOf(deductionLines, "aman_card"),
            amenGebakDeduction: amtOf(deductionLines, "amen_gebak"),
            premiumCardDeduction: amtOf(deductionLines, "premium_card"),
            computerInstallment: amtOf(deductionLines, "computer_installment"),
            rayaInstallment: amtOf(deductionLines, "raya_installment"),
            salaryAdvanceDeduction: amtOf(deductionLines, "salary_advance"),
            emergencyFundDeduction: amtOf(deductionLines, "emergency_fund"),
            medicalInsuranceDeduction: medicalInsuranceDeductionVal,

            // ── Per-line tax breakdown ──
            actualGrossSalary: roundToHundredth(actualGrossSalary),
            baseTax: roundToHundredth(baseTax),
            baseMartyrsFund: roundToHundredth(baseMartyrsFund),
            transportationTax: roundToHundredth(transportationTax),
            transportationMartyrsFund: roundToHundredth(transportationMartyrsFund),
            otherAllowancesTax: roundToHundredth(otherAllowancesTax),
            otherAllowancesMartyrsFund: roundToHundredth(otherAllowancesMartyrsFund),
            preTaxDeductionsTaxSaved: roundToHundredth(preTaxDeductionsTaxSaved),
            preTaxDeductionsMFSaved: roundToHundredth(preTaxDeductionsMFSaved),
          };

          // "Other Allowances"/"Other Deductions" catch-all buckets: now that
          // every named type above has its own column, these two are
          // recomputed to subtract off ALL of them (not just the original
          // four), so nothing is ever double-counted between a named column
          // and the catch-all lump sum.
          // NOTE: "kpiAllowance" is deliberately excluded — it reads the same
          // underlying line as "bonusAdjustments" (both keyed off the
          // kpi_adjustment slug), so it's shown for the invoice vocabulary
          // but not subtracted a second time here.
          // Derived from ALLOWANCE_SUM_KEYS rather than a second hand-kept
          // list, so the plug can never drift out of step with the sum the
          // Excel formula actually performs.
          const namedAllowanceKeys = ALLOWANCE_SUM_KEYS.filter((k) => k !== "otherAllowances");
          const namedAllowanceSum = namedAllowanceKeys.reduce((sum, k) => sum + (Number(rawValues[k]) || 0), 0);
          rawValues.otherAllowances = Math.max(0, roundToHundredth(emp.totalAllowances) - namedAllowanceSum);

          // "Other Deductions" is the earnings-reduction plug, and it is what
          // makes the live formulas reproduce the backend exactly. Work back
          // from figures the backend already computed rather than from the
          // deduction columns:
          //
          //   net package        = Net Salary + recoveries
          //   earnings reduction = Basic + Total Allowances − net package
          //
          // then hand the part no named column accounts for to this bucket.
          // Anchoring it this way means Σ EARNINGS_REDUCTION_KEYS in the
          // sheet always equals the backend's own earningsReductionDeductions,
          // so Excel's gross-up lands on the same Gross, Tax and Net that the
          // system paid against — for Mohamed Adel that is the 675 commission
          // clawback surfacing here instead of vanishing.
          //
          // NOTE: this bucket holds ONLY real earnings reductions. It must
          // never absorb tax, Martyrs' Fund or the insurance share (as it
          // used to, by subtracting from emp.totalDeductions) — feeding a
          // statutory withholding back into the net package would tax it a
          // second time.
          const netPackageActual = fullNetPackageVal;
          const earningsReductionActual =
            (Number(emp.basicSalary) || 0) + (Number(emp.totalAllowances) || 0) - netPackageActual;
          const namedEarningsReductionSum = EARNINGS_REDUCTION_KEYS
            .filter((k) => k !== "otherDeductions")
            .reduce((sum, k) => sum + (Number(rawValues[k]) || 0), 0);
          rawValues.otherDeductions = roundToHundredth(
            Math.max(0, earningsReductionActual - namedEarningsReductionSum),
          );

          // ── Live formulas for this row ────────────────────────────────
          // `here` is a cell on this sheet; `master` is the same row on the
          // Salary Sheet, which has every column. A derived value is
          // computed locally when this tab holds all its inputs, and
          // referenced from the Salary Sheet when it doesn't — never
          // recomputed a second, divergent way.
          const here = (k) => (sheetColLetters[k] ? `${sheetColLetters[k]}${R}` : null);
          const master = (k) => (salaryColLetters[k] ? `'${SALARY_SHEET_NAME}'!${salaryColLetters[k]}${R}` : null);
          const hasAll = (keys) => keys.every((k) => sheetColLetters[k]);
          const sumHere = (keys) => {
            const valid = keys.map((k) => here(k)).filter(Boolean);
            return valid.length > 0 ? valid.join("+") : "0";
          };
          // Prefer this sheet's own cell; fall back to the Salary Sheet's.
          const cell = (k) => here(k) || master(k);

          const f = {};
          const insCell = cell("insuranceSalary");

          if (hasAll(ALLOWANCE_SUM_KEYS)) f.allowances = sumHere(ALLOWANCE_SUM_KEYS);

          if (insCell) {
            f.socialInsurance = `${insCell}*0.11`;
            f.insuranceCompanyShare = `${insCell}*0.1875`;
          }

          if (isMaster) {
            const netPackage = `(${here("basicSalary")}+${here("allowances")}-(${sumHere(EARNINGS_REDUCTION_KEYS)}))`;
            const recoveries = `(${sumHere(POST_TAX_RECOVERY_KEYS)})`;
            // Gross before recoveries: closed-form gross-up formula (~850 chars, well under 8,192)
            if (here("actualGrossSalary")) {
              f.actualGrossSalary = xGrossUp(netPackage, here("insuranceSalary"));
              f.grossSalary = `${here("actualGrossSalary")}-${recoveries}`;
              // taxMonth references the computed Gross cell directly — keeping it under 470 chars!
              f.taxMonth = xTax(here("actualGrossSalary"), here("insuranceSalary"));
              f.martyrsFund = `${here("actualGrossSalary")}*0.0005`;
            } else {
              f.grossSalary = `${xGrossUp(netPackage, here("insuranceSalary"))}-${recoveries}`;
              f.taxMonth = xTax(here("grossSalary"), here("insuranceSalary"));
              f.martyrsFund = `${here("grossSalary")}*0.0005`;
            }
            f.netSalary = `${here("grossSalary")}-${here("socialInsurance")}-${here("taxMonth")}-${here("martyrsFund")}`;
          } else {
            if (here("actualGrossSalary")) f.actualGrossSalary = master("actualGrossSalary");
            f.grossSalary = master("grossSalary");
            f.taxMonth = master("taxMonth");
            f.martyrsFund = master("martyrsFund");
            f.netSalary = master("netSalary");
          }

          if (cell("grossSalary") && cell("netSalary")) {
            f.deductions = `${cell("grossSalary")}-${cell("netSalary")}`;
          }

          if (cell("grossSalary") && cell("insuranceCompanyShare") && cell("emergencyFund") && cell("medicalInsuranceDeduction")) {
            f.totalEmployeeCost = `${cell("grossSalary")}+${cell("insuranceCompanyShare")}+${cell("emergencyFund")}+${cell("medicalInsuranceDeduction")}`;
          }

          if (cell("totalEmployeeCost") && cell("markup")) {
            f.totalCost = `${cell("totalEmployeeCost")}+${cell("markup")}`;
          }

          if (insCell && here("oldNetSalary")) {
            if (here("oldGrossSalary")) {
              f.oldGrossSalary = xGrossUp(here("oldNetSalary"), insCell);
              f.oldTaxFromNet = xTax(here("oldGrossSalary"), insCell);
              f.oldMartyrsFundFromNet = `${here("oldGrossSalary")}*0.0005`;
              if (here("grossSalary")) {
                f.overtimeGross = `MAX(0,${here("grossSalary")}-${here("oldGrossSalary")})`;
              }
            }
          }

          if (hasAll(["taxMonth", "baseTax", "otherAllowancesTax", "preTaxDeductionsTaxSaved"])) {
            f.transportationTax = `${here("taxMonth")}-${here("baseTax")}-${here("otherAllowancesTax")}+${here("preTaxDeductionsTaxSaved")}`;
          }
          if (hasAll(["martyrsFund", "baseMartyrsFund", "otherAllowancesMartyrsFund", "preTaxDeductionsMFSaved"])) {
            f.transportationMartyrsFund = `${here("martyrsFund")}-${here("baseMartyrsFund")}-${here("otherAllowancesMartyrsFund")}+${here("preTaxDeductionsMFSaved")}`;
          }

          // Imported payrolls are locked: their figures are shown exactly as
          // they were imported, never recalculated. The payroll's own stored
          // totals (gross, net, tax, ...) and everything kept in
          // importedValues (insurance shares, tax breakdown, cost, markup, ...)
          // are written as plain values instead of the live formulas.
          if (emp.isImported) {
            IMPORTED_STORED_KEYS.forEach((k) => {
              delete f[k];
            });
            // What the import calculated for the values the file did not give,
            // overridden by what the file did give (imported values win).
            const stored = { ...(emp.calculatedValues?.values || {}), ...(emp.importedValues || {}) };
            Object.entries(IMPORTED_VALUE_KEY_TO_COL).forEach(([storedKey, colKey]) => {
              const raw = stored[storedKey];
              if (raw === undefined || raw === null || raw === "") return;
              const n = Number(String(raw).replace(/,/g, ""));
              if (!Number.isFinite(n)) return;
              rawValues[colKey] = roundToHundredth(n);
              delete f[colKey];
            });
          }

          sheetCols.forEach((col, colIdx) => {
            const formula = f[col.key] || null;
            const value = col.importedHeader !== undefined
              ? importedExtraValue(emp, col.importedHeader)
              : rawValues[col.key];
            ws[`${colLetter(colIdx)}${R}`] = makeDataCell(value, col, rowIdx, formula);
          });
        });

        // Summary row
        sheetCols.forEach((col, colIdx) => {
          const addr = `${colLetter(colIdx)}${summaryRow}`;
          if (colIdx === 0) {
            ws[addr] = makeSummaryCell(t("netTotal") ? `${t("netTotal")} (${numEmployees})` : `NET TOTAL (${numEmployees} Employees)`, false, true);
          } else if (col.type === "money" || col.type === "money2" || col.type === "moneyInt" || col.type === "formula") {
            // Cache the column's own total so the footer reads correctly
            // before Excel recalculates. Read back off the cells just written
            // rather than re-deriving it, so the two can never disagree.
            let colTotal = 0;
            for (let rr = firstDataRow; rr <= lastDataRow; rr++) {
              const cellAt = ws[`${colLetter(colIdx)}${rr}`];
              colTotal += Number(cellAt?.v) || 0;
            }
            ws[addr] = makeSummaryCell(`SUM(${sheetColLetters[col.key]}${firstDataRow}:${sheetColLetters[col.key]}${lastDataRow})`, true, true, colTotal);
          } else {
            ws[addr] = {
              v: "", t: "s",
              s: {
                font: { name: FONT_NAME, sz: FONT_SIZE, bold: true, color: { rgb: HEADER_FG } },
                fill: { fgColor: { rgb: HEADER_BG } },
                border: thinBorder(),
              },
            };
          }
        });

        // Imported-values disclaimer under the totals, whenever the sheet holds
        // an imported (locked) payroll.
        const importedCount = filteredPayrollData.filter((e) => e?.isImported).length;
        const disclaimerRow = importedCount > 0 ? summaryRow + 2 : null;
        if (disclaimerRow) {
          ws[`A${disclaimerRow}`] = {
            v: isRTL
              ? `تنبيه: ${importedCount} من الرواتب في هذا الملف مستوردة من Excel. القيم المستوردة محفوظة كما وردت في الملف ولم يراجعها النظام أو يعيد حسابها؛ المسؤول عن صحتها هو من قام بالاستيراد، والنظام غير مسؤول عن أي أخطاء حسابية فيها.`
              : `Note: ${importedCount} payroll(s) in this file were imported from Excel. Imported values are kept exactly as provided and are not checked or recalculated by the system; the importer is responsible for their accuracy, and the system is not responsible for miscalculations in imported values.`,
            t: "s",
            s: {
              font: { name: FONT_NAME, sz: FONT_SIZE, italic: true, color: { rgb: "9C5700" } },
              fill: { fgColor: { rgb: "FFF2CC" } },
              alignment: { horizontal: isRTL ? "right" : "left", vertical: "center", wrapText: true },
            },
          };
        }

        // Worksheet metadata
        ws["!ref"] = `A1:${colLetter(sheetCols.length - 1)}${disclaimerRow || summaryRow}`;
        ws["!cols"] = sheetCols.map((c) => ({ wch: c.wch }));
        ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: sheetCols.length - 1 } }];
        if (disclaimerRow) {
          ws["!merges"].push({
            s: { r: disclaimerRow - 1, c: 0 },
            e: { r: disclaimerRow - 1, c: Math.min(sheetCols.length - 1, 14) },
          });
        }
        if (isRTL) ws["!dir"] = "rtl";

        // Row heights
        const rowHeightsArr = [];
        rowHeightsArr[0] = { hpt: 30 }; // title row
        rowHeightsArr[1] = { hpt: 35 }; // header row
        for (let r = 2; r <= summaryRow; r++) {
          if (!rowHeightsArr[r]) rowHeightsArr[r] = { hpt: 20 };
        }
        if (disclaimerRow) rowHeightsArr[disclaimerRow - 1] = { hpt: 42 };
        ws["!rows"] = rowHeightsArr;

        // Freeze title + header rows
        ws["!sheetViews"] = [{ state: "frozen", ySplit: 2, topLeftCell: "A3", activePane: "bottomLeft" }];

        return ws;
      };

      // ── Build & save workbook ────────────────────────────────────────────────
      const wb = XLSX.utils.book_new();
      // Recalculate everything on open. The cached values written with each
      // formula make the sheet readable immediately; this makes Excel refresh
      // them rather than trusting a cache that a later edit may have staled.
      wb.Workbook = { ...(wb.Workbook || {}), CalcPr: { fullCalcOnLoad: true } };
      
      const wsSalarySheet = buildWorksheet(SALARY_SHEET_COLS, periodString, true);
      const wsInvoice = buildWorksheet(INVOICE_COLS, periodString);
      const wsAllowances = buildWorksheet(ALLOWANCES_COLS, periodString);
      const wsDeductions = buildWorksheet(DEDUCTIONS_COLS, periodString);
      const wsInfo = buildWorksheet(INFO_COLS, periodString);

      XLSX.utils.book_append_sheet(wb, wsSalarySheet, SALARY_SHEET_NAME);
      XLSX.utils.book_append_sheet(wb, wsInvoice, isRTL ? "الفاتورة" : "Invoice");
      XLSX.utils.book_append_sheet(wb, wsAllowances, isRTL ? "البدلات" : "Allowances");
      XLSX.utils.book_append_sheet(wb, wsDeductions, isRTL ? "الخصومات" : "Deductions");
      XLSX.utils.book_append_sheet(wb, wsInfo, isRTL ? "المعلومات" : "Information");

      XLSX.writeFile(wb, `Payroll_${exportYear}-${exportMonth}.xlsx`);
      showAlert(t("success"), `${t("payrollExportSuccess")}`, "success");

    } catch (error) {
      console.error("Error exporting payroll to Excel:", error);
      showAlert(
        t("error"),
        `${t("payrollExportError")}: ${error.message}`,
        "error",
      );
    }
  };

  const handleExportPayrollPDF = async () => {
    const filteredPayrollData = fetchAllFiltered ? await fetchAllFiltered() : loadedRows;
    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: html2canvas } = await import("html2canvas");

      const isAr = language === "ar";
      const exportYear = Number(
        selectedMonth?.year || new Date().getFullYear(),
      );
      const exportMonth = String(
        Number(selectedMonth?.month || new Date().getMonth() + 1),
      ).padStart(2, "0");
      const totalGross = roundToInt(
        filteredPayrollData.reduce(
          (s, e) => s + (Number(e.grossSalary) || 0),
          0,
        ),
      );
      const totalDed = roundToHundredth(
        filteredPayrollData.reduce(
          (s, e) => s + (Number(e.totalDeductions) || 0),
          0,
        ),
      );
      const totalNet = roundToInt(
        filteredPayrollData.reduce((s, e) => s + (Number(e.netSalary) || 0), 0),
      );
      const currency = t("currency");
      const dir = isAr ? "rtl" : "ltr";

      const rowsHtml = filteredPayrollData
        .map(
          (emp, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f3f6fb"}">
        <td>${emp.infinityCode || emp.infinitycode || emp.code || "-"}</td>
        <td>${emp.englishName || emp.employeeName || "-"}</td>
        <td>${emp.arabicName || "-"}</td>
        <td>${emp.nationalId || "-"}</td>
        <td>${emp.phoneNumber || "-"}</td>
        <td>${emp.department || "-"}</td>
        <td>${roundToInt(emp.grossSalary).toLocaleString()}</td>
        <td>${roundToHundredth(emp.basicSalary).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td style="font-weight:700;color:#16a34a">${roundToInt(emp.netSalary).toLocaleString()}</td>
        <td>${roundToInt(emp.workingDays)}</td>
        <td>${roundToInt(emp.absenceDays || 0)}</td>
        <td>${roundToHundredth(emp.absenceDeduction).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${roundToHundredth(emp.totalAllowances || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${roundToHundredth(emp.penalty).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${roundToHundredth(emp.advancePayment).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${roundToHundredth(emp.socialInsurance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${roundToHundredth(emp.sickLeaveDeduction).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${roundToHundredth(emp.delayDeduction).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${roundToHundredth(emp.totalDeductions).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td>${emp.status || "Active"}</td>
      </tr>`,
        )
        .join("");

      // ── Build an off-screen container the browser renders natively ──────────
      // Using Tahoma / Arial — both ship with Windows and include Arabic glyphs.
      const wrapper = document.createElement("div");
      wrapper.style.cssText = [
        "position:fixed",
        "left:-99999px",
        "top:0",
        "width:1122px",
        "background:#fff",
        `font-family:Tahoma,Arial,'Segoe UI',sans-serif`,
        `direction:${dir}`,
        "color:#1e293b",
        "padding:24px",
        "z-index:-9999",
      ].join(";");

      wrapper.innerHTML = `
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        .ph{text-align:center;margin-bottom:20px}
        .ph h1{font-size:20px;font-weight:700;color:#2563eb;margin-bottom:4px}
        .ph p{font-size:11px;color:#64748b}
        table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:24px}
        th{background:#2563eb;color:#fff;font-weight:700;padding:8px 6px;text-align:center}
        td{padding:7px 6px;border-bottom:1px solid #e2e8f0;text-align:center}
        .sb{border:1px solid #e2e8f0;border-radius:8px;padding:14px 18px;max-width:380px;${isAr ? "margin-right:auto" : "margin-left:auto"}}
        .sb h3{font-size:13px;font-weight:700;color:#2563eb;margin-bottom:10px}
        .sr{display:flex;justify-content:space-between;padding:4px 0;font-size:11px;border-bottom:1px solid #f1f5f9}
        .sr.net{font-size:13px;font-weight:700;border-top:2px solid #1e293b;border-bottom:none;padding-top:8px;margin-top:4px}
      </style>
      <div class="ph">
        <h1>${t("payrollDetails")}</h1>
        <p>${t("exportDate")}: ${new Date().toLocaleDateString(isAr ? "ar-EG" : "en-GB")} &nbsp;|&nbsp; ${t("payPeriod")}: ${exportYear}-${exportMonth}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th>${t("infinitycode")}</th>
            <th>${t("englishName") !== "englishName" ? t("englishName") : (isRTL ? "الاسم بالانجليزي" : "English Name")}</th>
            <th>${t("arabicName")}</th>
            <th>${t("nationalId")}</th>
            <th>${t("phone")}</th>
            <th>${t("department")}</th>
            <th>${t("grossSalary") !== "grossSalary" ? t("grossSalary") : (isRTL ? "إجمالي الراتب" : "Gross Salary")}</th>
            <th>${t("basicSalary")}</th>
            <th>${t("netSalary") !== "netSalary" ? t("netSalary") : (isRTL ? "صافي الراتب" : "Net Salary")}</th>
            <th>${t("workingDays")}</th>
            <th>${t("absenceDays") !== "absenceDays" ? t("absenceDays") : (isRTL ? "أيام الغياب" : "Absence Days")}</th>
            <th>${t("absenceDeduction") !== "absenceDeduction" ? t("absenceDeduction") : (isRTL ? "قيمة الغياب" : "Absence Value")}</th>
            <th>${t("allowances")}</th>
            <th>${t("penalty") !== "penalty" ? t("penalty") : (isRTL ? "الجزاءات" : "Penalty")}</th>
            <th>${t("advancePayment") !== "advancePayment" ? t("advancePayment") : (isRTL ? "السلف" : "Advance")}</th>
            <th>${t("socialInsurance") !== "socialInsurance" ? t("socialInsurance") : (isRTL ? "التأمينات" : "Insurance")}</th>
            <th>${t("sickLeave") !== "sickLeave" ? t("sickLeave") : (isRTL ? "إجازة مرضية 25%" : "Sick Leave 25%")}</th>
            <th>${t("delay") !== "delay" ? t("delay") : (isRTL ? "تأخير" : "Delay")}</th>
            <th>${t("deductions")}</th>
            <th>${t("status")}</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="sb">
        <h3>${t("payrollSummary")}</h3>
        <div class="sr"><span>${t("employees")}:</span><span>${filteredPayrollData.length}</span></div>
        <div class="sr"><span>${t("grossTotal")}:</span><span>${totalGross.toLocaleString()} ${currency}</span></div>
        <div class="sr"><span>${t("deductions")}:</span><span>${totalDed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}</span></div>
        <div class="sr net"><span>${t("netTotal")}:</span><span>${totalNet.toLocaleString()} ${currency}</span></div>
      </div>`;

      document.body.appendChild(wrapper);

      // Let the browser paint before capturing
      await new Promise((r) => setTimeout(r, 150));

      const canvas = await html2canvas(wrapper, {
        scale: 1.8,
        useCORS: false,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: 1122,
      });

      document.body.removeChild(wrapper);

      // ── Embed canvas image into a landscape A4 PDF (with auto-pagination) ──
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: "a4",
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * pageW) / canvas.width;
      const imgData = canvas.toDataURL("image/jpeg", 0.93);

      let remaining = imgH;
      let offsetY = 0;

      pdf.addImage(imgData, "JPEG", 0, offsetY, imgW, imgH);
      remaining -= pageH;

      while (remaining > 0) {
        offsetY -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, offsetY, imgW, imgH);
        remaining -= pageH;
      }

      pdf.save(`Payroll_${exportYear}-${exportMonth}.pdf`);
      showAlert(t("success"), `${t("payrollExportSuccessPDF")}`, "success");
    } catch (error) {
      console.error("Error exporting payroll to PDF:", error);
      showAlert(
        t("error"),
        `${t("payrollExportErrorPDF")}: ${error.message}`,
        "error",
      );
    }
  };

  return {
    handleExportPayrollExcel,
    handleExportPayrollPDF,
  };
};

export default usePayrollExport;
